import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { submitBetaTester, BetaTester } from '../utils/supabase';
import './BetaTesterForm.css';

const AppleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const AndroidIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-1.39-.59-2.94-.92-4.57-.92s-3.18.33-4.57.92L5.45 5.67c-.18-.28-.54-.37-.83-.22-.3.16-.42.54-.26.85L6.2 9.48C2.72 11.32 0 15.01 0 19.5h24c0-4.49-2.72-8.18-6.4-10.02zM7 15.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm10 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
  </svg>
);

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const BetaTesterForm: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState<BetaTester['platform'] | null>(null);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!platform) {
      setErrorMessage('Please select a platform');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      await submitBetaTester({
        name,
        email,
        platform,
      });
      setStatus('success');
      setName('');
      setEmail('');
      setPlatform(null);
    } catch (error: any) {
      console.error('Error submitting:', error);
      setStatus('error');
      if (error.code === '23505') {
        setErrorMessage('This email is already registered for beta testing.');
      } else {
        setErrorMessage(error.message || 'Failed to submit. Please try again.');
      }
    }
  };

  if (status === 'success') {
    return (
      <section className="beta-tester" id="beta-tester">
        <div className="beta-tester__container">
          <div className="beta-tester__success">
            <CheckCircle size={48} className="beta-tester__success-icon" />
            <h3>You're on the list!</h3>
            <p>Thanks for signing up. RalliPB is not currently available on the Apple App Store or Google Play, so we'll contact you with beta and store availability updates.</p>
            <button
              className="btn btn--primary"
              onClick={() => setStatus('idle')}
            >
              Sign Up Another Platform
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="beta-tester" id="beta-tester">
      <div className="beta-tester__container">
        <div className="section-header">
          <div className="section-badge">Beta Waitlist</div>
          <h2>RalliPB is <span className="text-gradient">not in app stores yet</span></h2>
          <p className="section-subtitle">
            RalliPB is not currently available on the Apple App Store or Google Play. Sign up for iOS or Android beta updates while public availability is pending.
          </p>
          <div className="beta-tester__notice" role="status">
            This form does not provide an App Store or Google Play download link. It adds you to the RalliPB beta waitlist.
          </div>
        </div>

        <form className="beta-tester__form" onSubmit={handleSubmit}>
          <div className="beta-tester__row">
            <div className="beta-tester__field">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
              />
            </div>
            <div className="beta-tester__field">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div className="beta-tester__field">
            <label>Which platform are you interested in?</label>
            <div className="beta-tester__platforms">
              <button
                type="button"
                className={`beta-tester__platform ${platform === 'ios' ? 'beta-tester__platform--active' : ''}`}
                onClick={() => setPlatform('ios')}
              >
                <AppleIcon />
                <span>iOS</span>
              </button>
              <button
                type="button"
                className={`beta-tester__platform ${platform === 'android' ? 'beta-tester__platform--active' : ''}`}
                onClick={() => setPlatform('android')}
              >
                <AndroidIcon />
                <span>Android</span>
              </button>
            </div>
          </div>

          {errorMessage && (
            <div className="beta-tester__error">
              <AlertCircle size={16} />
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="btn btn--primary btn--lg beta-tester__submit"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? (
              'Signing Up...'
            ) : (
              <>
                <Send size={18} />
                Join Beta Waitlist
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default BetaTesterForm;
